/**
 * The one unsaved-changes registry a router needs (TMMAPP-0073). react-router honours a single
 * blocker at a time — with several `useBlocker`s mounted it consults only the most recently
 * registered and warns on every navigation — while a page can hold several editors (an inline
 * form and two dialogs). So the app mounts ONE `useBlocker` (`UnsavedGuardProvider`, in the app
 * shell) and every editor registers a *source* here; the blocker holds a navigation while any
 * source is dirty, and the most recently dirtied source — behind a modal only the topmost dialog can
 * be edited — is the one that asks the question.
 */
import { createContext } from 'react';
import type { Location, NavigationType } from 'react-router-dom';

export interface GuardSource {
  /** The editor has unsaved work. */
  dirty: boolean;
  /** A save is in flight: nothing is held (the post-save redirect must not prompt). */
  busy: boolean;
  /** Also hold a change to `?search` on the same path (an editor inside a tab). */
  includeSearch: boolean;
  /** Shows (`true`) or clears (`false`) this editor's discard question. */
  ask: (asking: boolean) => void;
  /**
   * An open dialog's own close (TMMAPP-0097 §C 26). When a page editor's question is about to
   * show, an open dialog with nothing unsaved closes first, so the question never opens on top of
   * it (DLG-005). Absent for a page editor and for a dialog that is not open.
   */
  dismissClean?: (() => void) | undefined;
}

export interface GuardRegistry {
  /** Adds a source (on top of the stack); returns its removal. */
  register: (source: GuardSource) => () => void;
  /** The source just turned dirty: it moves to the top (the most recently dirtied one asks). */
  touch: (source: GuardSource) => void;
  /** "Discard changes": the held navigation continues. Clears every question. */
  proceed: () => void;
  /** "Keep editing": the held navigation is dropped. Clears every question. */
  stay: () => void;
}

export const UnsavedGuardContext = createContext<GuardRegistry | null>(null);

/** `/auth/*` leaves through a hard redirect (sign-out, an expired session) and is never held. */
const AUTH_PATH = /^\/auth(\/|$)/;

/**
 * The navigation state a page's own, programmatic query write carries (TMMAPP-0082): `useUrlState`'s
 * `replace` writes (a debounced search, a stale filter cleaned up on load). A dirty editor holds
 * what the person does — Back / Forward and the links and tabs they use — never what the page writes
 * to its own URL for itself.
 */
export const BACKGROUND_WRITE_STATE = { backgroundWrite: true } as const;

/**
 * A page's own query write: a `REPLACE` that carries the marker and stays on the same path. The
 * action is required: a Back to an entry an earlier write marked is a `POP`, which is the person's.
 */
export function isBackgroundWrite(
  current: Location,
  next: Location,
  action: NavigationType | undefined,
): boolean {
  return (
    action === 'REPLACE' &&
    current.pathname === next.pathname &&
    (next.state as { backgroundWrite?: unknown } | null)?.backgroundWrite === true
  );
}

/** Would this source hold the move from `current` to `next`? */
export function sourceBlocks(
  source: GuardSource,
  current: Location,
  next: Location,
  action?: NavigationType,
): boolean {
  if (!source.dirty || source.busy) return false;
  if (AUTH_PATH.test(next.pathname)) return false;
  if (isBackgroundWrite(current, next, action)) return false;
  if (current.pathname !== next.pathname) return true;
  return source.includeSearch && current.search !== next.search;
}

/** The last source in the stack that holds the move (the most recently dirtied one), if any. */
export function topBlockingSource(
  sources: Iterable<GuardSource>,
  current: Location,
  next: Location,
  action?: NavigationType,
): GuardSource | undefined {
  let top: GuardSource | undefined;
  for (const source of sources) {
    if (sourceBlocks(source, current, next, action)) top = source;
  }
  return top;
}
